import { Alert, Button, Modal, TextAreaField, TextField, useConfirm, useToast } from '@cartly/ui';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../api/client';
import { useSubmit } from '../../app/hooks';
import type { QuickReply } from './types';

/** Team-wide canned replies, inserted in the composer with `/shortcut`. */
export function QuickRepliesManager({
  items,
  onClose,
  onChanged,
}: {
  items: QuickReply[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const form = useSubmit();
  const [editing, setEditing] = useState<QuickReply | null>(null);
  const [shortcut, setShortcut] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const reset = () => {
    setEditing(null);
    setShortcut('');
    setTitle('');
    setBody('');
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await form.run(async () => {
      const payload = { shortcut, title, body };
      if (editing) await api.patch(`/inbox/quick-replies/${editing.id}`, payload);
      else await api.post('/inbox/quick-replies', payload);
      return true;
    });
    if (ok) {
      reset();
      onChanged();
    }
  };

  const remove = async (item: QuickReply) => {
    const ok = await confirm({
      title: `Delete /${item.shortcut}?`,
      message: 'Agents will no longer be able to insert this reply.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/inbox/quick-replies/${item.id}`);
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Modal open title="Quick replies" onClose={onClose}>
      <div className="stack">
        {items.length === 0 ? (
          <p className="field__hint">
            No quick replies yet. Add the answers you send most often; agents insert them by typing
            / in a reply.
          </p>
        ) : (
          <ul className="quick-list">
            {items.map((r) => (
              <li key={r.id}>
                <div>
                  <strong>/{r.shortcut}</strong> <span>{r.title}</span>
                  <p>{r.body}</p>
                </div>
                <div className="quick-list__actions">
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setEditing(r);
                      setShortcut(r.shortcut);
                      setTitle(r.title);
                      setBody(r.body);
                    }}
                  >
                    Edit
                  </button>
                  <button type="button" className="link-button" onClick={() => void remove(r)}>
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <form className="stack" onSubmit={(e) => void save(e)}>
          <h3 className="section-title">
            {editing ? `Edit /${editing.shortcut}` : 'Add a quick reply'}
          </h3>
          {form.error && <Alert tone="critical">{form.error}</Alert>}
          <div className="form-grid">
            <TextField
              label="Shortcut"
              value={shortcut}
              onChange={(e) => setShortcut(e.target.value)}
              hint="Typed as /shortcut"
              error={form.fieldErrors['shortcut']}
              maxLength={40}
            />
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={form.fieldErrors['title']}
              maxLength={120}
            />
          </div>
          <TextAreaField
            label="Reply text"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            error={form.fieldErrors['body']}
            maxLength={2000}
          />
          <div className="form-actions">
            {editing && (
              <Button onClick={reset} disabled={form.pending}>
                Cancel edit
              </Button>
            )}
            <Button type="submit" variant="primary" loading={form.pending}>
              {editing ? 'Save changes' : 'Add quick reply'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
